/**
 * ConnectedSourcesTab — Modo operacional do Cérebro.
 *
 * v0.99.11 · Fev/2026 — Toggle entre "Autônomo" e "Integrado".
 *
 *   AUTÔNOMO  → dono cadastra tudo aqui (default, profissional sem ERP)
 *   INTEGRADO → dados vêm de um sistema externo (Tecnofit, Feegow, …)
 *               via Integration Engine. Abas de cadastro viram read-only.
 *
 * Um lugar único de decisão. Nem toda academia usa Tecnofit; nem toda
 * clínica usa Feegow. O toggle mora aqui para o dono declarar quem
 * abastece o Cérebro.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PlugsConnected, PencilSimple, ShieldCheck, ArrowsClockwise } from "@phosphor-icons/react";

export default function ConnectedSourcesTab({ onChange, onToast }) {
  const [mode, setMode]         = useState("autonomous");
  const [srcLabel, setSrcLabel] = useState("");
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/operacao/mode");
      setMode(data.mode || "autonomous");
      setSrcLabel(data.source_label || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar modo do Cérebro");
    } finally {
      setLoading(false);
    }
  };

  const save = async (nextMode, nextLabel = "") => {
    setSaving(true);
    try {
      const body = { mode: nextMode };
      if (nextMode === "integrated") body.source_label = nextLabel.trim() || null;
      await api.put("/operacao/mode", body);
      setMode(nextMode);
      setSrcLabel(nextMode === "integrated" ? (nextLabel.trim()) : "");
      onToast?.(
        nextMode === "integrated"
          ? `Modo integrado ativo${nextLabel ? ` com ${nextLabel}` : ""}. Cadastros do Cérebro agora refletem seu sistema.`
          : "Modo autônomo ativo. Você mesmo alimenta o Cérebro por aqui.",
      );
      onChange?.();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Erro ao trocar modo", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 p-6">Carregando…</div>;
  }

  return (
    <div data-testid="connected-sources-tab" className="space-y-6">
      {/* Explicação — a promessa AgentUS */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
            <PlugsConnected size={16} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-sm font-semibold text-slate-900 tracking-tight">
              Como o Cérebro é abastecido?
            </h3>
            <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
              O AgentUS <strong>não é um ERP</strong> — é o cérebro que conversa no WhatsApp.
              Você escolhe de onde vêm os dados: cadastrando aqui (para quem não tem sistema
              de gestão) ou puxando do seu ERP (quando existe).
            </p>
          </div>
        </div>
      </div>

      {/* Cards de escolha */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="mode-options">
        {/* Autônomo */}
        <ModeCard
          testId="mode-card-autonomous"
          active={mode === "autonomous"}
          icon={PencilSimple}
          title="Sou autônomo"
          subtitle="Cadastro tudo aqui"
          bullets={[
            "Recomendado para profissionais e pequenos negócios sem ERP.",
            "Serviços, produtos, horários e ofertas ficam editáveis no Cérebro.",
            "O AgentUS usa exatamente o que você cadastrar — nada mais.",
          ]}
          onClick={() => mode !== "autonomous" && !saving && save("autonomous")}
          disabled={saving}
        />

        {/* Integrado */}
        <ModeCard
          testId="mode-card-integrated"
          active={mode === "integrated"}
          icon={ArrowsClockwise}
          title="Tenho sistema de gestão"
          subtitle="Meus dados vêm do ERP"
          bullets={[
            "O AgentUS lê seus serviços, agenda e clientes direto do seu sistema.",
            "As abas de cadastro do Cérebro viram consulta (sem edição duplicada).",
            "Você atualiza no ERP, o cérebro segue vivo automaticamente.",
          ]}
          onClick={() => {}}
          disabled={saving}
        >
          {/* Editor do label do ERP quando este card já está ativo */}
          {mode === "integrated" && (
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
              <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-500 font-medium">
                Qual sistema alimenta o Cérebro?
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={srcLabel}
                  onChange={(e) => setSrcLabel(e.target.value)}
                  placeholder="Ex: Tecnofit, Feegow, Sponte, Bling…"
                  data-testid="integrated-source-input"
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900"
                />
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => save("integrated", srcLabel)}
                  data-testid="integrated-source-save"
                  className="px-3 py-2 bg-slate-900 text-white text-sm rounded-md hover:bg-slate-800 disabled:opacity-50"
                >
                  Salvar
                </button>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Este rótulo aparece nos badges das abas do Cérebro (ex: &ldquo;Sincronizado com Tecnofit&rdquo;).
                O conector real ao ERP virá pelo Marketplace de Integrações.
              </p>
            </div>
          )}
          {/* CTA para ativar quando ainda não está ativo */}
          {mode !== "integrated" && (
            <button
              type="button"
              disabled={saving}
              onClick={() => save("integrated", srcLabel || "")}
              data-testid="mode-integrated-activate"
              className="mt-4 w-full px-3 py-2 bg-white border border-slate-300 hover:border-slate-900 text-slate-900 text-sm rounded-md disabled:opacity-50 transition-colors"
            >
              Ativar modo integrado
            </button>
          )}
        </ModeCard>
      </div>

      {/* Status atual */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3" data-testid="mode-status">
        <ShieldCheck size={18} className="text-emerald-600 shrink-0 mt-0.5" weight="fill" />
        <div className="text-[12.5px] text-slate-700 leading-relaxed">
          <strong className="text-slate-900">Status atual:</strong>{" "}
          {mode === "autonomous"
            ? "Cérebro autônomo — você alimenta os cadastros manualmente por aqui."
            : `Cérebro integrado${srcLabel ? ` com ${srcLabel}` : ""} — as abas de cadastro estão sincronizadas com seu sistema.`}
        </div>
      </div>
    </div>
  );
}

function ModeCard({ testId, active, icon: Icon, title, subtitle, bullets, onClick, disabled, children }) {
  return (
    <div
      role="button"
      aria-pressed={active}
      onClick={disabled ? undefined : onClick}
      data-testid={testId}
      className={`rounded-xl p-5 border transition-all ${
        active
          ? "border-slate-900 bg-slate-900 text-white shadow-sm"
          : "border-slate-200 bg-white hover:border-slate-400 cursor-pointer"
      } ${disabled ? "opacity-70" : ""}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
          active ? "bg-white/10 text-white" : "bg-slate-100 text-slate-700"
        }`}>
          <Icon size={16} weight={active ? "fill" : "regular"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-heading font-semibold tracking-tight ${active ? "text-white" : "text-slate-900"}`}>
            {title}
          </div>
          <div className={`text-[11px] uppercase tracking-[0.14em] mt-0.5 ${active ? "text-white/60" : "text-slate-500"}`}>
            {subtitle}
          </div>
        </div>
        {active && (
          <span className="text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-white/15 text-white font-semibold">
            Ativo
          </span>
        )}
      </div>
      <ul className={`mt-4 space-y-1.5 text-[12.5px] ${active ? "text-white/85" : "text-slate-600"}`}>
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2 leading-relaxed">
            <span className="mt-1.5 w-1 h-1 rounded-full shrink-0 bg-current opacity-70" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {children}
    </div>
  );
}
